import { useModalAction, useModalState } from "@/contexts/modal/Context";
import { ExerciseTODO, LoginUserItem, QuestionItem } from "@/types";
import { useNavigate } from "react-router-dom";
import routes from "@/config/routes.ts";
import dayjs, { Dayjs } from "dayjs";
import {
  useCorrectExerciseMutation,
  useUserExercises,
} from "@/data/exercises.ts";
import { useEffect, useState } from "react";
import duration from "dayjs/plugin/duration";
import { enqueueSnackbar } from "notistack";
import utc from "dayjs/plugin/utc";
import { SpinnerLoader } from "@/components/ui/Loader.tsx";
import TrashBinIcon from "../icons/TrashBinICon";
import { useExerciseAction } from "@/contexts/exercise/Context";
import { t } from "i18next";
import Button from "@edusogno/primitives/button";
import LoadingButton from "@edusogno/primitives/loading-button";
import useGlobals from "@/utils/hooks/useGlobals";
import { maixpanelTrackEventProps } from "@/mixpanel/MixpanelUtils";
import { useMe } from "@/data/user";

dayjs.extend(duration);
dayjs.extend(utc);

interface DoExerciseActionModalState {
  type: "submit" | "exit";
  title: string;
  message: string | JSX.Element;
  timeStart?: Dayjs;
  id: string;
}

export default function DoExerciseActionModal() {
  const { data } = useModalState();
  const { closeModal } = useModalAction();

  const { title, type, message, timeStart, id } =
    data as DoExerciseActionModalState;

  const navigate = useNavigate();
  const [exercise, setExercise] = useState<ExerciseTODO | undefined>(undefined);
  const { exercises, isLoading: fetchingExercise } = useUserExercises();
  const [isLoading, setIsLoading] = useState(false);
  const { endSequence } = useExerciseAction();
  const { isPot } = useGlobals();

  const { mutateAsync: correctExercise } = useCorrectExerciseMutation();
  function compareAnswers(
    correctAnswers: QuestionItem,
    userAnswers: Record<number, string>,
  ) {
    const incorrectAnswers: Record<
      number,
      { correct_answer: string; user_answer: string }
    > = {};
    const regex = /\(([^)]+)\)/;
    for (const [questionNo, question] of Object.entries(correctAnswers)) {
      const questionNumber = parseInt(questionNo);
      const correctAnswer = question.correct_answer?.toLowerCase() ?? "";
      const match = correctAnswer.match(regex);

      let correctedAnswers: string[] = [];

      if (match) {
        correctedAnswers.push(
          correctAnswer.replace(match[0], match[1]).replace(/\s+/g, " ").trim(),
        );
        correctedAnswers.push(
          correctAnswer.replace(match[0], "").replace(/\s+/g, " ").trim(),
        );
      } else {
        correctedAnswers = correctAnswer.split("/").map((e) => e.trim());
      }

      if (
        !correctedAnswers.includes(
          userAnswers[questionNumber]
            ?.toLowerCase()
            .replace(/\s+/g, " ")
            .trim(),
        )
      ) {
        incorrectAnswers[questionNumber] = {
          correct_answer: question.correct_answer,
          user_answer:
            userAnswers[questionNumber] === ""
              ? "N/A"
              : userAnswers[questionNumber],
        };
      }
    }
    return incorrectAnswers;
  }

  const prepareSubmit = async () => {
    if (!exercise) return;
    const currentTime = dayjs();

    const currentDuration = dayjs.duration(currentTime.diff(timeStart));
    const formattedDuration = dayjs
      .utc(currentDuration.asMilliseconds())
      .format("HH:mm:ss");

    setIsLoading(true);
    const form = document.getElementById("form-ex");
    if (!form) {
      setIsLoading(false);
      return;
    }
    const formInputs = [...form.querySelectorAll("input")];
    const formData = [...formInputs].filter((el) =>
      el.tagName.match(/^INPUT$/),
    );

    const userData: Record<number, string> = {};

    if (
      exercise.type === "Listening" ||
      exercise.type === "Reading" ||
      exercise.type === "Grammar"
    ) {
      formData.forEach((el, i) => {
        userData[i + 1] = el.value;
      });

      const userSubmit = compareAnswers(exercise.questions, userData);

      const submitData = {
        user_submit: JSON.stringify(userSubmit),
        token_ex: exercise.token || "",
        test_name: exercise.name,
        type: exercise.type,
        timer: formattedDuration,
      };

      const response = await correctExercise({
        id: exercise.id,
        ...submitData,
      });

      if (response) {
        if (response[0].type !== "Writing") {
          navigate(
            `${routes.exercises}/correction/${exercise.type?.toLowerCase()}/${exercise.token || ""}`,
            { replace: true },
          );
        } else {
          navigate(routes.exercises, {
            replace: true,
            state: { tab: 1 },
          });
        }
        closeModal();
      }
    }

    setIsLoading(false);
  };

  const prepareWriting = async () => {
    if (!exercise) return;
    setIsLoading(true);
    const formData = document.getElementById("form-ex");

    if (!formData) {
      setIsLoading(false);
      return;
    }

    const userData: string[] = [];
    [
      ...[
        ...formData.querySelectorAll("textarea"),
        ...formData.querySelectorAll("input"),
      ],
    ].forEach((el) => {
      userData.push(el?.value);
    });

    const filtered = userData.filter((element) => {
      return element?.length > 10;
    });

    if (filtered.length === 0) {
      setIsLoading(false);
      enqueueSnackbar("You cannot submit an empty exercise");
      return;
    }

    const submitData = {
      user_submit: JSON.stringify(filtered),
      token_ex: exercise.token || "",
      test_name: exercise.name,
      type: exercise.type,
    };

    const response = await correctExercise({
      id: exercise?.id,
      ...submitData,
    });

    if (response) {
      if (response[0].type !== "Writing") {
        navigate(
          `${routes.exercises}/correction/${exercise.type?.toLowerCase()}/${exercise.token || ""}`,
          { replace: true },
        );
      } else {
        navigate(routes.exercises, {
          replace: true,
          state: { tab: "todo" },
        });
      }
      closeModal();
    }
  };

  const handleYes = () => {
    if (type === "exit") {
      if (isPot) {
        endSequence();
      }
      closeModal();
      navigate(routes.exercises);
      return;
    }

    if (exercise?.type !== "Writing") {
      void prepareSubmit().then(() => { });
    } else {
      void prepareWriting().then(() => { });
    }
  };

  useEffect(() => {
    if (exercises && exercises.toDo) {
      const ex: ExerciseTODO | undefined = exercises.toDo.find(
        (exe: ExerciseTODO) => exe.token === id
      );
      setExercise(ex);
    }

  }, [exercises, id]);

  // mixpanel integration
  const { me } = useMe();
  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Exercise: Do Action Modal", me?.item?.user as LoginUserItem),
  );

  return (
    <>
      <div className="flex flex-col items-stretch h-full gap-4">
        {fetchingExercise ? (
          <div className="flex items-center justify-center w-full h-full">
            <SpinnerLoader className={"!h-12 !w-12 border-t-light"} />
          </div>
        ) : (
          <div>
            <div
              className={
                "flex flex-col h-full justify-center items-center gap-10"
              }
            >
              {type === "exit" && (
                <TrashBinIcon />
              )}
              <p className="text-2xl font-semibold leading-6 text-brand">
                {title}
              </p>
              <p className="text-sm font-normal leading-4">{message}</p>
              <div
                className={
                  "flex flex-row items-center justify-center w-full gap-4 mt-2"
                }
              >
                <Button
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={`Excersie_Do_Action_Modal_${t("btns.no")}`}
                  disabled={isLoading} type="submit" onClick={closeModal}>
                  {/* No */}
                  {t("btns.no")}
                </Button>
                <LoadingButton
                  data-track-props={mixpanelPropsData}
                  data-track-eventtype={`Excersie_Do_Action_Modal_${t("btns.yes")}`}
                  color={"green"}
                  loading={isLoading}
                  disabled={isLoading}
                  onClick={() => void handleYes()}
                >
                  {t("btns.yes")}
                </LoadingButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
